import { useEffect, useMemo, useState } from 'react';
import { Modal } from './Modals.jsx';
import * as db from '../lib/data.js';
import NameTh from './NameTh.jsx';
import PermitPanel, { PermitChips, usePermitIndex } from './PermitPanel.jsx';
import { checkCard, mustLine, summarizePermits } from '../lib/permit-flags.js';

// อ่านไฟล์ invoice/PI แล้วให้ตรวจก่อนบันทึก — 3 กอง ตามที่ user ขอ (25 ต.ค. 2569):
//   "สร้างใบรายละเอียดสินค้าให้เอง โดยแจ้งให้เพิ่มเฉพาะสินค้าที่ยังไม่เคยมี หรือต้องการการแก้ไข"
//
//   ✓ ข้อมูลครบ   = มีใบข้อมูลสินค้า + พิกัดยืนยันแล้ว → ไม่ต้องทำอะไร (เงียบ ย่อไว้)
//   ! ต้องเติม    = มีใบข้อมูลสินค้า แต่ยังขาดคำบรรยาย/วัสดุ/พิกัด → แตะเพื่อไปเติม
//   ＋ ยังไม่เคยมี = ไม่มีในทะเบียน → ต้องสร้างใบใหม่
//   เรื่องใบอนุญาตแยกต่างหาก (2 พ.ย. 2569): ✓ ข้อมูลครบ ไม่ได้แปลว่าไม่ต้องขอใบอนุญาต ⇒ แถวที่ติดป้ายห้ามถูกพับ
//
// ⚠ กติกาที่ห้ามพัง:
//   ① ไม่เขียนอะไรลงฐานจนกว่าจะกดยืนยัน — อ่านไฟล์อย่างเดียวไม่ใช่การบันทึก
//   ② ไม่เดา "จำนวนต่อกล่อง" จากข้อความในไฟล์ · โชว์ช่องให้คนใส่เอง
//      (เลขนี้เดาผิดแล้วราคาต่อชิ้นผิดเท่าจำนวนในกล่อง โดยยอดรวมยังลงตัว = จับด้วยตาไม่ได้)
//   ③ ราคาต่อชิ้นโชว์ข้างทุกบรรทัด ให้คนเห็นว่าเลขสมเหตุสมผลไหมก่อนกด

const PACK_UNIT = /\b(ctn|carton|case|box|pack|dz|dozen|กล่อง|ลัง|โหล)\b/i;

export default function ImportInvoiceModal({ file, consignment, onClose, onDone }) {
  const [stage, setStage] = useState('reading');   // reading | map | review | saving
  const [err, setErr] = useState(null);
  const [parsed, setParsed] = useState(null);      // { headers, rows }
  const [mapping, setMapping] = useState({});
  const [lines, setLines] = useState([]);          // แถวที่ผ่านการจับคู่แล้ว
  const [fees, setFees] = useState([]);            // บรรทัดที่ไม่ใช่สินค้า (ค่าขนส่ง/ส่วนลด)
  const permit = usePermitIndex();

  // ── อ่านไฟล์ ────────────────────────────────────────────────────────────
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        // โหลดตัวแกะไฟล์แบบ lazy — xlsx + pdf.js รวมกันหนัก ไม่ควรติดไปกับหน้าแรก
        const pi = await import('../lib/pi-parse.js');
        const { headers, rows } = await pi.parseFile(file);
        if (dead) return;
        const guess = pi.guessMapping(headers);
        setParsed({ headers, rows });
        setMapping(guess);
        if (pi.isComplete(guess)) await toReview(headers, rows, guess, pi);
        else setStage('map');
      } catch (e) {
        if (!dead) { setErr(e.message ?? String(e)); setStage('error'); }
      }
    })();
    return () => { dead = true; };
  }, [file]);

  // ── จับคู่กับทะเบียนสินค้า ───────────────────────────────────────────────
  const toReview = async (headers, rows, map, piMod) => {
    const pi = piMod ?? await import('../lib/pi-parse.js');
    const raw = pi.applyMapping(headers, rows, map);
    const products = raw.filter(l => l.lineType === 'product');
    setFees(raw.filter(l => l.lineType !== 'product'));

    const cards = await db.matchProductCards(products.map(l => l.code));
    setLines(products.map(l => {
      const card = cards[db.normSku(l.code)] ?? null;
      const needs = card ? db.cardGaps(card) : null;
      return {
        ...l,
        card,
        bucket: !card ? 'new' : (needs.length ? 'fix' : 'ok'),
        gaps: needs ?? [],
        pack: 1,
        keep: true,
      };
    }));
    setStage('review');
  };

  // i = ตำแหน่งจริงใน lines (แถวถูกแบ่งกอง/คัดลอก indexOf ใช้ไม่ได้)
  const view = useMemo(() => lines.map((l, i) => ({
    ...l, i, permit: permit.index ? checkCard(l.card, permit.index, l.desc) : null,
  })), [lines, permit.index]);
  const buckets = useMemo(() => {
    const chip = (l) => !!l.permit && l.permit.level !== 'quiet';
    return {
      ok:  view.filter(l => l.bucket === 'ok'),
      okFlag: view.filter(l => l.bucket === 'ok' && chip(l)),
      okQuiet: view.filter(l => l.bucket === 'ok' && !chip(l)),
      fix: view.filter(l => l.bucket === 'fix'),
      new: view.filter(l => l.bucket === 'new'),
    };
  }, [view]);
  const permitLines = useMemo(() => view.filter(l => l.permit).map(l => ({
    key: `${l.code}|${l.i}`, sku: l.code, name: l.card ? (l.card.name_en || l.card.name_th) : l.desc,
    cardId: l.card?.id ?? null, result: l.permit, on: l.keep,
  })), [view]);
  const needsAck = view.some(l => l.keep && (l.permit?.level === 'must' || l.permit?.level === 'check'));

  const setLine = (i, patch) => setLines(ls => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  // เตือนเรื่องกล่อง: หน่วยบน invoice ดูเหมือนกล่องแต่ยังใส่จำนวนต่อกล่องเป็น 1
  const packWarn = lines.some(l =>
    l.keep && l.pack === 1 && PACK_UNIT.test(`${l.desc} ${l.code}`));

  const save = async () => {
    setStage('saving'); setErr(null);
    try {
      const payload = lines.filter(l => l.keep).map(l => ({
        sku: l.code, description: l.desc, qty: l.qty,
        unit_price: l.unit, pack_size: l.pack, unit: 'ชิ้น',
      }));
      const r = await db.importInvoiceLines(consignment.id, payload, consignment.shipment?.id);
      const asked = mustLine(summarizePermits(permitLines.filter(l => l.on !== false)));
      onDone?.(r, buckets.new.filter(l => l.keep).map(l => ({ sku: l.code, name: l.desc })), asked);
      onClose();
    } catch (e) { setErr(e.message ?? String(e)); setStage('review'); }
  };

  // ── หน้าจอ ───────────────────────────────────────────────────────────────
  if (stage === 'reading') return (
    <Modal title="กำลังอ่านไฟล์…" sub={file.name} onClose={onClose}>
      <p className="ii-wait">ไฟล์ PDF ที่หน้ามาก ๆ อาจใช้เวลาสักครู่ — ยังไม่มีอะไรถูกบันทึก</p>
    </Modal>
  );

  if (stage === 'error') return (
    <Modal title="อ่านไฟล์นี้ไม่ได้" sub={file.name} onClose={onClose}>
      <div className="ferr">{err}</div>
      <div className="ii-help">
        อ่านได้: PDF ที่พิมพ์ออกจากระบบ · Excel (.xlsx .xls) · CSV<br/>
        อ่านไม่ได้: PDF ที่เป็นรูปสแกน หรือรูปถ่าย — กรณีนี้ขอให้ช่วยขอไฟล์ Excel จากซัพพลายเออร์
        หรือกด “＋ เลือกเอง” ติ๊กสินค้าจากคลังแทน
      </div>
      <div className="mfoot"><button className="btn2" onClick={onClose}>ปิด</button></div>
    </Modal>
  );

  if (stage === 'map') return (
    <Modal title="ไฟล์นี้หัวตารางไม่เหมือนที่เคยเจอ" wide
      sub="ฝากช่วยบอกว่าคอลัมน์ไหนคืออะไร — อย่างน้อยต้องมี รหัสสินค้า กับ จำนวน" onClose={onClose}>
      <div className="ii-map">
        {parsed.headers.map(h => (
          <label className="ii-mrow" key={h}>
            <span className="ii-mh">{h || '(ไม่มีชื่อ)'}</span>
            <select value={mapping[h] ?? ''} onChange={e => {
              const v = e.target.value;
              setMapping(m => {
                const next = { ...m };
                for (const k of Object.keys(next)) if (next[k] === v) delete next[k];
                if (v) next[h] = v; else delete next[h];
                return next;
              });
            }}>
              <option value="">— ไม่ใช้ —</option>
              <option value="supplier_product_code">รหัสสินค้า</option>
              <option value="description">ชื่อสินค้า</option>
              <option value="qty">จำนวน</option>
              <option value="unit_price">ราคา/หน่วย</option>
              <option value="amount">รวม</option>
            </select>
          </label>
        ))}
      </div>
      <div className="ii-preview">
        <div className="ii-ct">3 แถวแรกในไฟล์</div>
        <table><tbody>
          {parsed.rows.slice(0, 3).map((r, i) => (
            <tr key={i}>{r.slice(0, 8).map((c, k) => <td key={k}>{String(c).slice(0, 22)}</td>)}</tr>
          ))}
        </tbody></table>
      </div>
      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ยกเลิก</button>
        <button className="btn1"
          disabled={!['supplier_product_code', 'qty'].every(f => Object.values(mapping).includes(f))}
          onClick={() => toReview(parsed.headers, parsed.rows, mapping)}>ดูรายการที่อ่านได้</button>
      </div>
    </Modal>
  );

  // ⚠ ฟังก์ชันธรรมดา ไม่ใช่คอมโพเนนต์ใน render — เดิมเป็น <Row/> ทุกครั้งที่ state เปลี่ยนแถวถูกสร้างใหม่ ช่อง × หลุดโฟกัสทุกตัวอักษร
  const rowView = (l) => (
    <div className={`ii-row b-${l.bucket} ${l.keep ? '' : 'off'}`} key={`${l.code}|${l.i}`}>
      <input type="checkbox" checked={l.keep} onChange={e => setLine(l.i, { keep: e.target.checked })}
        aria-label={`เอา ${l.code} เข้าใบนี้`}/>
      <span className="ii-sku">{l.code}</span>
      <span className="ii-name"><NameTh c={l.card} fallback={l.desc} missing={!!l.card}/></span>
      <span className="ii-qty">{l.qty}</span>
      <label className="ii-pack" title="1 หน่วยบน invoice = กี่ชิ้น — ถ้าซัพส่งเป็นกล่อง ใส่จำนวนในกล่องตรงนี้ ระบบจะหารราคาให้เอง">
        <span>×</span>
        <input type="number" min="1" value={l.pack}
          onChange={e => setLine(l.i, { pack: Math.max(1, Math.round(Number(e.target.value) || 1)) })}/>
      </label>
      <span className="ii-pc">
        {l.unit ? `${(l.unit / l.pack).toLocaleString(undefined, { maximumFractionDigits: 2 })}/ชิ้น` : '—'}
      </span>
      {l.bucket === 'fix' && <span className="ii-gap">ขาด {l.gaps.join(' · ')}</span>}
      {l.bucket === 'new' && <span className="ii-gap">ยังไม่เคยมีในทะเบียน</span>}
      <PermitChips result={l.permit} open={!!l.why} onToggle={() => setLine(l.i, { why: !l.why })}/>
    </div>
  );

  return (
    <Modal title="ตรวจก่อนใส่เข้าใบข้อมูลสินค้า" wide
      sub={`${file.name} · อ่านได้ ${lines.length} รายการ — ยังไม่บันทึกจนกว่าจะกดยืนยัน`} onClose={onClose}>
      {err && <div className="ferr">{err}</div>}

      <PermitPanel mode="import" status={permit.status} lines={permitLines} onRetry={permit.retry}/>

      <div className="ii-sum">
        <span className="ii-chip ok">✓ ข้อมูลครบ {buckets.ok.length}</span>
        <span className="ii-chip fix">! ยังขาดข้อมูล {buckets.fix.length}</span>
        <span className="ii-chip new">＋ ยังไม่เคยมี {buckets.new.length}</span>
      </div>

      <div className="ii-note">
        <b>✓ ข้อมูลครบ</b> หมายถึงใบข้อมูลสินค้ามีข้อมูลครบสำหรับส่ง broker ไม่รวมเรื่องใบอนุญาต ซึ่งดูจากกล่องด้านบน
        {(buckets.fix.length > 0 || buckets.new.length > 0) && <> · ที่เหลือ {buckets.fix.length + buckets.new.length} รายการ ฝากช่วยเติมข้อมูลก่อนส่ง broker ทำได้หลังกดยืนยัน</>}
      </div>

      {buckets.new.length > 0 && (
        <><div className="ii-h new">＋ ยังไม่เคยมีในทะเบียน ({buckets.new.length})</div>
          {buckets.new.map(rowView)}</>
      )}
      {buckets.fix.length > 0 && (
        <><div className="ii-h fix">! มีใบข้อมูลแล้ว แต่ยังขาดบางช่อง ({buckets.fix.length})</div>
          {buckets.fix.map(rowView)}</>
      )}
      {buckets.okFlag.length > 0 && (
        <><div className="ii-h pm">● ข้อมูลสินค้าครบ แต่ฝากดูเรื่องใบอนุญาต ({buckets.okFlag.length})</div>
          {buckets.okFlag.map(rowView)}</>
      )}
      {buckets.okQuiet.length > 0 && (
        <details className="ii-fold">
          <summary>✓ ข้อมูลครบ{permit.index ? ' และไม่พบเรื่องใบอนุญาตในระบบ' : ''} {buckets.okQuiet.length} รายการ — กดเพื่อดู</summary>
          {buckets.okQuiet.map(rowView)}
        </details>
      )}

      {fees.length > 0 && (
        <div className="ii-fees">
          ข้ามไป {fees.length} บรรทัดที่ไม่ใช่สินค้า ({fees.slice(0, 3).map(f => f.desc || f.code).join(' · ')}
          {fees.length > 3 ? ' …' : ''}) — ค่าขนส่ง/ส่วนลดพวกนี้ไม่ต้องลงใบข้อมูลสินค้า
        </div>
      )}

      {packWarn && (
        <div className="ii-warn">
          บางบรรทัดมีคำที่แปลว่า “กล่อง/ลัง” แต่ช่อง <b>×</b> ยังเป็น 1 —
          ถ้าซัพคิดเป็นกล่อง ฝากช่วยใส่จำนวนชิ้นในกล่องก่อน ไม่งั้นราคาต่อชิ้นจะผิดเท่าจำนวนในกล่อง
        </div>
      )}

      {needsAck && (
        <div className="ii-after">กดแล้วรายการจะเข้าใบนี้ และเรื่องใบอนุญาตจะขึ้นเตือนต่อที่หน้าใบข้อมูลสินค้า · ขั้นตอนนี้ยังไม่ได้ยื่นอะไรให้หน่วยงาน</div>
      )}

      <div className="mfoot ii-foot">
        <button className="btn2" onClick={onClose}>ยกเลิก</button>
        <button className="btn1" disabled={stage === 'saving' || !lines.some(l => l.keep)} onClick={save}>
          {stage === 'saving' ? 'กำลังบันทึก…' : `${needsAck ? 'รับทราบ · ' : ''}ใส่ ${lines.filter(l => l.keep).length} รายการเข้าใบนี้`}
        </button>
      </div>
    </Modal>
  );
}
